import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Facebook, Twitter, Instagram, Youtube, Mail, Phone, MapPin, ExternalLink, ArrowRight, Linkedin, FileText } from 'lucide-react';
import { NewsletterForm } from '../forms/NewsletterForm';
import { REGISTERED_CHARITY_LABEL } from '../../constants/charity';
import { PHONE_NUMBER, PHONE_TEL_HREF } from '../../constants/contact';
import { scheduleScrollToResourcesFilters } from '../../constants/resources';
import { scheduleScrollToProjectsFilters, PROJECTS_FILTERS_ID } from '../../constants/projects';
import { scheduleScrollToPlanningFilters, PLANNING_FILTERS_ID } from '../../constants/planning';
import { scheduleScrollToElementById } from '../../constants/scrollNavigation';

const footerLinkClassName =
  'text-white/70 hover:text-cpre-mint text-sm transition-colors duration-200 flex items-center gap-1 group text-left';

const aboutLinks = [
  { name: 'Our Story', pathname: '/about', hash: '#story', scrollId: 'story' },
  { name: 'Our Team', pathname: '/about', hash: '#team', scrollId: 'team' },
  { name: 'Governance', pathname: '/governance', hash: '#governance-intro', scrollId: 'governance-intro' },
  { name: 'Annual Reports', pathname: '/governance', hash: '#annual-reports', scrollId: 'annual-reports' },
  { name: 'Press & Media', pathname: '/about', hash: '#press', scrollId: 'press' },
];

const projectLinks = [
  { name: 'All Projects', pathname: '/projects', hash: '#projects-filters', scrollId: 'projects-filters' },
  { name: 'Centenary Projects', pathname: '/centenary', hash: '#centenary-projects', scrollId: 'centenary-projects' },
  { name: 'Habitat Restoration', pathname: '/projects', search: '?category=habitat', hash: '#projects-filters', scrollId: 'projects-filters' },
  { name: 'Access & Trails', pathname: '/projects', search: '?category=trails', hash: '#projects-filters', scrollId: 'projects-filters' },
  { name: 'Planning Alerts', pathname: '/planning', hash: '#planning-filters', scrollId: 'planning-filters' },
];

const getInvolvedLinks = [
  { name: 'Volunteer', pathname: '/volunteering', hash: '#volunteer-opportunities', scrollId: 'volunteer-opportunities' },
  { name: 'Donate', pathname: '/donate', hash: '#donate-main', scrollId: 'donate-main' },
  { name: 'Become a Member', pathname: '/donate', hash: '#membership', scrollId: 'membership' },
  { name: 'Corporate Partners', pathname: '/donate', hash: '#partners', scrollId: 'partners' },
  { name: 'Leave a Legacy', pathname: '/donate', hash: '#legacy', scrollId: 'legacy' },
];

const resourceLinks = [
  { name: 'Planning Guidance', category: 'planning' },
  { name: 'Policy Documents', category: 'policy' },
  { name: 'Reports & Research', category: 'reports' },
  { name: 'Educational Materials', category: 'education' },
];

const governanceLinks = [
  { name: 'Constitution', href: `${process.env.REACT_APP_BACKEND_URL}/api/uploads/2024/07/RSCC-Constitution-AGM-Approved-19.6.2024.pdf`, external: true },
  { name: 'Code of Conduct', href: `${process.env.REACT_APP_BACKEND_URL}/api/uploads/2024/07/RSCC-Board-Member-Code-of-Conduct.17.6.2024.pdf`, external: true },
  { name: 'Conflict of Interest Policy', href: `${process.env.REACT_APP_BACKEND_URL}/api/uploads/2021/11/CPRE-Rutland-Conflicts-of-Interests-Policy.-December-2021.-V1.pdf`, external: true },
  { name: 'Financial Regulations', href: `${process.env.REACT_APP_BACKEND_URL}/api/uploads/2021/12/CPRE-Rutland-Financial-Regulations-V1-December-2021.pdf`, external: true },
  { name: 'Equality & Diversity Policy', href: `${process.env.REACT_APP_BACKEND_URL}/api/uploads/2024/07/RSCC-Equality-and-Diversity-Policy.17.6.2024-002.pdf`, external: true },
  { name: 'Procurement Policy', href: `${process.env.REACT_APP_BACKEND_URL}/api/uploads/2024/07/RSCC-Procurement-Policy-19.6.2024.pdf`, external: true },
  { name: 'Annual Report 2024', href: `${process.env.REACT_APP_BACKEND_URL}/api/uploads/2024/07/CPRE-Rutland-Annual-Report-2024.Final_.pdf`, external: true },
];

export const Footer = () => {
  const navigate = useNavigate();

  const goToResourceCategory = (category) => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    navigate({ pathname: '/resources', search: `?category=${category}` });
    scheduleScrollToResourcesFilters({ delayMs: 100 });
  };

  const goToFooterTarget = ({ pathname, search = '', hash = '', scrollId }) => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    navigate({ pathname, search, hash });
    if (scrollId === PROJECTS_FILTERS_ID) {
      scheduleScrollToProjectsFilters({ delayMs: 100 });
    } else if (scrollId === PLANNING_FILTERS_ID) {
      scheduleScrollToPlanningFilters({ delayMs: 100 });
    } else {
      scheduleScrollToElementById(scrollId, { delayMs: 100 });
    }
  };

  return (
    <footer className="bg-cpre-new-yew text-white" data-testid="main-footer">
      {/* Newsletter Banner */}
      <div className="border-b border-white/10">
        <div className="container-custom py-12">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-8">
            <div className="text-center lg:text-left">
              <h3 className="font-heading text-2xl font-bold mb-2 text-white">Stay Updated</h3>
              <p className="text-white/80">Get the latest countryside news delivered to your inbox.</p>
            </div>
            <div className="w-full lg:w-auto">
              <NewsletterForm variant="footer" />
            </div>
          </div>
        </div>
      </div>

      {/* Main Footer Content */}
      <div className="container-custom py-16">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-8 lg:gap-10">
          {/* Brand Column with CPRE Logo */}
          <div className="col-span-2 md:col-span-3 lg:col-span-2">
            {/* Official CPRE Centenary Logo for dark background */}
            <div className="mb-6">
              <img 
                src="/cpre-logo-dark-bg.png" 
                alt="CPRE Rutland and Lincolnshire - 100 Years" 
                className="h-16 w-auto"
                style={{ minHeight: '50px' }}
              />
            </div>
            <p className="text-white/80 text-sm leading-relaxed mb-2 max-w-sm">
              For 100 years, CPRE has been protecting and enhancing the countryside of Rutland and Lincolnshire. We campaign for a beautiful, thriving countryside for everyone to enjoy.
            </p>
            <p className="text-white/60 text-sm mb-6">{REGISTERED_CHARITY_LABEL}</p>
            
            {/* Social Links */}
            <div className="flex gap-3 mb-6">
              {[
                { icon: Facebook, href: 'https://facebook.com/cpre', label: 'Facebook' },
                { icon: Twitter, href: 'https://twitter.com/cpre', label: 'Twitter' },
                { icon: Instagram, href: 'https://instagram.com/cpre', label: 'Instagram' },
                { icon: Youtube, href: 'https://www.youtube.com/user/CPRE1926', label: 'YouTube' },
                { icon: Linkedin, href: 'https://linkedin.com/company/cpre', label: 'LinkedIn' },
              ].map(({ icon: Icon, href, label }) => (
                <a 
                  key={label}
                  href={href} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center hover:bg-cpre-hill transition-colors duration-200"
                  aria-label={label}
                  data-testid={`social-${label.toLowerCase()}`}
                >
                  <Icon className="w-5 h-5" />
                </a>
              ))}
            </div>

            {/* Contact Info */}
            <div className="space-y-2 text-sm text-white/70">
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4" />
                <span>chair@cprerutlandandlincs.uk</span>
              </div>
              <a href={PHONE_TEL_HREF} className="flex items-center gap-2 hover:text-cpre-mint transition-colors duration-200">
                <Phone className="w-4 h-4" />
                <span>{PHONE_NUMBER}</span>
              </a>
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                <span>Oakham, Rutland</span>
              </div>
            </div>
          </div>

          {/* About CPRE */}
          <div>
            <h4 className="font-heading font-semibold text-base mb-5 text-white">About CPRE</h4>
            <ul className="space-y-3">
              {aboutLinks.map((link) => (
                <li key={link.name}>
                  <button
                    type="button"
                    onClick={() => goToFooterTarget(link)}
                    className={footerLinkClassName}
                  >
                    <span>{link.name}</span>
                    <ArrowRight className="w-3 h-3 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-200" />
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Projects */}
          <div>
            <h4 className="font-heading font-semibold text-base mb-5 text-white">Projects</h4>
            <ul className="space-y-3">
              {projectLinks.map((link) => (
                <li key={link.name}>
                  <button
                    type="button"
                    onClick={() => goToFooterTarget(link)}
                    className={footerLinkClassName}
                  >
                    <span>{link.name}</span>
                    <ArrowRight className="w-3 h-3 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-200" />
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Get Involved */}
          <div>
            <h4 className="font-heading font-semibold text-base mb-5 text-white">Get Involved</h4>
            <ul className="space-y-3">
              {getInvolvedLinks.map((link) => (
                <li key={link.name}>
                  <button
                    type="button"
                    onClick={() => goToFooterTarget(link)}
                    className={footerLinkClassName}
                  >
                    <span>{link.name}</span>
                    <ArrowRight className="w-3 h-3 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-200" />
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Resources */}
          <div>
            <h4 className="font-heading font-semibold text-base mb-5 text-white">Resources</h4>
            <ul className="space-y-3">
              {resourceLinks.map((link) => (
                <li key={link.name}>
                  <button
                    type="button"
                    onClick={() => goToResourceCategory(link.category)}
                    className="text-white/70 hover:text-cpre-mint text-sm transition-colors duration-200 flex items-center gap-1 group text-left"
                  >
                    <span>{link.name}</span>
                    <ArrowRight className="w-3 h-3 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-200" />
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Governance */}
          <div>
            <h4 className="font-heading font-semibold text-base mb-5 text-white">Governance</h4>
            <ul className="space-y-3">
              {governanceLinks.map((link) => (
                <li key={link.name}>
                  <a 
                    href={link.href} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-white/70 hover:text-cpre-mint text-sm transition-colors duration-200 flex items-center gap-1 group"
                  >
                    <FileText className="w-3 h-3 flex-shrink-0" />
                    <span>{link.name}</span>
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="container-custom py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex flex-wrap items-center justify-center md:justify-start gap-4 text-sm text-white/60">
              <span>© {new Date().getFullYear()} CPRE Rutland & Lincolnshire</span>
              <span className="hidden sm:inline">•</span>
              <span>{REGISTERED_CHARITY_LABEL}</span>
            </div>
            <div className="flex items-center gap-6 text-sm">
              <Link to="/privacy" className="text-white/60 hover:text-cpre-mint transition-colors duration-200">
                Privacy Policy
              </Link>
              <Link to="/terms" className="text-white/60 hover:text-cpre-mint transition-colors duration-200">
                Terms of Use
              </Link>
              <button
                type="button"
                onClick={() =>
                  toast.info('Coming Soon', {
                    description: 'Accessibility information will be available here soon.',
                  })
                }
                className="text-white/60 hover:text-cpre-mint transition-colors duration-200"
              >
                Accessibility
              </button>
              <a 
                href="https://www.cpre.org.uk" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-cpre-harvest-gold hover:text-cpre-mint flex items-center gap-1 transition-colors duration-200 font-medium"
                data-testid="national-cpre-link"
              >
                National CPRE
                <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
